import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
  ArrowLeft, User, Calendar, Clock, Play, Pause, 
  CheckCircle, AlertTriangle, Send, Star, FileText,
  MessageCircle, Shield, Heart, Sparkles, Users
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverAuditDetail = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const [scores, setScores] = useState({
    rapport_building: 5,
    active_listening: 5,
    emotional_attunement: 5,
    question_quality: 5,
    child_engagement: 5,
    session_flow: 5,
    safety_awareness: 5
  });
  
  const [feedback, setFeedback] = useState({
    strengths: '',
    areas_for_improvement: '',
    specific_recommendations: '',
    overall_notes: ''
  });
  
  const [outcome, setOutcome] = useState('approved');

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadAudit();
  }, [navigate, auditId]);

  const loadAudit = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/audit/${auditId}`);
      setAudit(response.data);
      
      // If there's existing data, pre-populate
      if (response.data.audit_result) {
        if (response.data.audit_result.scores) {
          setScores(response.data.audit_result.scores);
        }
        if (response.data.audit_result.feedback) {
          setFeedback(response.data.audit_result.feedback);
        }
        if (response.data.audit_result.outcome) {
          setOutcome(response.data.audit_result.outcome);
        }
      }
    } catch (error) {
      console.error('Error loading audit:', error);
      alert('Failed to load audit details');
      navigate('/observer/audits');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageScore = () => {
    const values = Object.values(scores);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const submitAudit = async () => {
    setSubmitting(true);
    try {
      const avgScore = parseFloat(calculateAverageScore());
      
      await axios.post(
        `${BACKEND_URL}/api/audit/${auditId}/submit`,
        null,
        {
          params: {
            rapport_building: scores.rapport_building,
            active_listening: scores.active_listening,
            emotional_attunement: scores.emotional_attunement,
            question_quality: scores.question_quality,
            child_engagement: scores.child_engagement,
            session_flow: scores.session_flow,
            safety_awareness: scores.safety_awareness,
            strengths: feedback.strengths,
            areas_for_improvement: feedback.areas_for_improvement,
            specific_recommendations: feedback.specific_recommendations,
            overall_notes: feedback.overall_notes,
            outcome: outcome
          }
        }
      );
      
      alert('Audit submitted successfully!');
      navigate('/observer/audits');
    } catch (error) {
      console.error('Error submitting audit:', error);
      alert('Failed to submit audit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ScoreSlider = ({ label, icon: Icon, color, scoreKey }) => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} />
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-lg font-bold ${color}`}>{scores[scoreKey]}/10</span>
      </div>
      <Slider
        value={[scores[scoreKey]]}
        onValueChange={([value]) => setScores({ ...scores, [scoreKey]: value })}
        max={10}
        min={1}
        step={1}
        className="w-full"
      />
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>Needs Work</span>
        <span>Excellent</span>
      </div>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <p>Audit not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/observer/audits')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Audits
            </Button>
            
            <h1 className="text-2xl font-bold text-gray-900">Session Audit</h1>
            <p className="text-gray-600">Review and score this observer session</p>
          </div>

          {/* Session Info Card */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-violet-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {audit.child_name || 'Child Session'}
                    </h2>
                    <p className="text-gray-500">Observer: {audit.original_observer_name}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(audit.session_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {audit.session_duration || '5'} min
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Average Score */}
                <div className="text-center">
                  <div className="text-4xl font-bold text-violet-600">{calculateAverageScore()}</div>
                  <p className="text-sm text-gray-500">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recording Player (Mock) */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-500" />
                Session Recording
              </h3>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 w-1/3"></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1:42</span>
                      <span>5:00</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4 italic">
                  Note: Audio playback is simulated in this demo. In production, actual session recordings would be available here.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Scoring Section */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Quality Scores
              </h3>
              
              <div className="grid md:grid-cols-2 gap-x-8">
                <ScoreSlider 
                  label="Rapport Building" 
                  icon={Heart} 
                  color="text-pink-500" 
                  scoreKey="rapport_building" 
                />
                <ScoreSlider 
                  label="Active Listening" 
                  icon={MessageCircle} 
                  color="text-blue-500" 
                  scoreKey="active_listening" 
                />
                <ScoreSlider 
                  label="Emotional Attunement" 
                  icon={Sparkles} 
                  color="text-purple-500" 
                  scoreKey="emotional_attunement" 
                />
                <ScoreSlider 
                  label="Question Quality" 
                  icon={MessageCircle} 
                  color="text-green-500" 
                  scoreKey="question_quality" 
                />
                <ScoreSlider 
                  label="Child Engagement" 
                  icon={Users} 
                  color="text-orange-500" 
                  scoreKey="child_engagement" 
                />
                <ScoreSlider 
                  label="Session Flow" 
                  icon={Clock} 
                  color="text-cyan-500" 
                  scoreKey="session_flow" 
                />
                <ScoreSlider 
                  label="Safety Awareness" 
                  icon={Shield} 
                  color="text-red-500" 
                  scoreKey="safety_awareness" 
                />
              </div>
            </CardContent>
          </Card>

          {/* Feedback Section */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-500" />
                Written Feedback
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Strengths Observed
                  </label>
                  <Textarea
                    value={feedback.strengths}
                    onChange={(e) => setFeedback({ ...feedback, strengths: e.target.value })}
                    placeholder="What did the observer do well?"
                    className="h-24"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Areas for Improvement
                  </label>
                  <Textarea
                    value={feedback.areas_for_improvement}
                    onChange={(e) => setFeedback({ ...feedback, areas_for_improvement: e.target.value })}
                    placeholder="What could be improved?"
                    className="h-24"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Recommendations
                  </label>
                  <Textarea
                    value={feedback.specific_recommendations}
                    onChange={(e) => setFeedback({ ...feedback, specific_recommendations: e.target.value })}
                    placeholder="Any specific tips or suggestions?"
                    className="h-24"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Overall Notes
                  </label>
                  <Textarea
                    value={feedback.overall_notes}
                    onChange={(e) => setFeedback({ ...feedback, overall_notes: e.target.value })}
                    placeholder="Any additional observations..."
                    className="h-20"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outcome Selection */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Audit Outcome</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => setOutcome('approved')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    outcome === 'approved' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <CheckCircle className={`w-8 h-8 mx-auto mb-2 ${outcome === 'approved' ? 'text-green-500' : 'text-gray-400'}`} />
                  <p className={`font-medium ${outcome === 'approved' ? 'text-green-700' : 'text-gray-700'}`}>Approved</p>
                  <p className="text-xs text-gray-500 mt-1">Session meets standards</p>
                </button>
                
                <button
                  onClick={() => setOutcome('needs_improvement')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    outcome === 'needs_improvement' 
                      ? 'border-yellow-500 bg-yellow-50' 
                      : 'border-gray-200 hover:border-yellow-300'
                  }`}
                >
                  <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${outcome === 'needs_improvement' ? 'text-yellow-500' : 'text-gray-400'}`} />
                  <p className={`font-medium ${outcome === 'needs_improvement' ? 'text-yellow-700' : 'text-gray-700'}`}>Needs Work</p>
                  <p className="text-xs text-gray-500 mt-1">Some areas need attention</p>
                </button>
                
                <button
                  onClick={() => setOutcome('flagged')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    outcome === 'flagged' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-red-300'
                  }`}
                >
                  <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${outcome === 'flagged' ? 'text-red-500' : 'text-gray-400'}`} />
                  <p className={`font-medium ${outcome === 'flagged' ? 'text-red-700' : 'text-gray-700'}`}>Flagged</p>
                  <p className="text-xs text-gray-500 mt-1">Requires review</p>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/observer/audits')}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAudit}
              disabled={submitting}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {submitting ? 'Submitting...' : 'Submit Audit'}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverAuditDetail;
